/**
 * AIS target symbology — turns a target's ship type and (where known) its real
 * size into an oriented, coloured chart outline, so at a glance you can tell a
 * small target from a big one and roughly what kind of vessel it is.
 *
 * Convention: an under-way target points along its heading (COG if no heading),
 * like a plotter/ECDIS triangle. We keep that triangle for most ships but vary
 * the OUTLINE for the categories where shape genuinely helps the eye: big
 * commercial traffic (cargo/tanker) gets a longer hull, fishing a diamond, and
 * sailing a slim sail-like wedge. A stopped/moored target (no real speed) has no
 * meaningful heading, so it draws as a small non-directional diamond instead of a
 * triangle that would point an arbitrary way.
 *
 * COLOUR comes from the vessel's category (cargo, tanker, passenger, and so on),
 * so a tanker reads differently from a yacht without having to tap it. Category
 * is taken from the raw AIS ship-type CODE when we have it (the canonical value),
 * falling back to the stored type label otherwise.
 *
 * SIZE comes from the AIS-reported length (to-bow + to-stern); when that's missing
 * we fall back to a sensible typical length for the category, so an unknown target
 * still draws at a believable size. The outline is geographic (nautical miles), so
 * to stop it ballooning zoomed in and shrinking to nothing offshore the caller
 * scales it for zoom with aisSymbolScaleForZoom — the same trick the wind barb uses.
 */
import { rhumbDestination } from '@/nav';
import type { AisTarget } from '@/store/ais';

export type VesselCategory =
  | 'cargo'
  | 'tanker'
  | 'passenger'
  | 'hsc'
  | 'tug'
  | 'fishing'
  | 'sailing'
  | 'pleasure'
  | 'other';

/**
 * Map a raw AIS ship-type code (0..99) to a drawing category. This is the
 * canonical mapping — the numeric code is what a static-data frame actually
 * carries. The ranges follow the AIS/ECDIS conventions: 30 fishing, 36 sailing,
 * 37 pleasure, the rest of 30-39 and all of 50-59 are working/special craft (tug,
 * dredger, pilot, SAR, law, and so on), 40-49 high-speed craft, 60-69 passenger,
 * 70-79 cargo, 80-89 tanker. Anything else (including 0 "not available") is
 * unknown/other. The three specific leisure codes are checked before their decade
 * so a yacht doesn't get lumped in with the tugs.
 */
export function categoryForShipType(code?: number): VesselCategory {
  if (code == null || !Number.isFinite(code)) return 'other';
  if (code === 30) return 'fishing';
  if (code === 36) return 'sailing';
  if (code === 37) return 'pleasure';
  if (code >= 31 && code <= 39) return 'tug'; // towing, dredging, diving, military, …
  if (code >= 40 && code <= 49) return 'hsc';
  if (code >= 50 && code <= 59) return 'tug'; // pilot, SAR, tug, port tender, law, …
  if (code >= 60 && code <= 69) return 'passenger';
  if (code >= 70 && code <= 79) return 'cargo';
  if (code >= 80 && code <= 89) return 'tanker';
  return 'other';
}

/** Map an AIS ship-type LABEL (as we also store it) to a drawing category. Used
 *  as a fallback when a target has a label but no numeric code yet. */
export function vesselCategory(shipType?: string): VesselCategory {
  switch ((shipType || '').toLowerCase()) {
    case 'cargo':
      return 'cargo';
    case 'tanker':
      return 'tanker';
    case 'passenger':
      return 'passenger';
    case 'high-speed craft':
      return 'hsc';
    case 'tug / towing':
      return 'tug';
    case 'fishing':
      return 'fishing';
    case 'sailing':
      return 'sailing';
    case 'pleasure craft':
      return 'pleasure';
    // Pilot, Search & rescue, Law enforcement, Other and unknown all fall here.
    default:
      return 'other';
  }
}

/** A target's drawing category: prefer the raw AIS code, fall back to the label. */
export function targetCategory(t: Pick<AisTarget, 'shipTypeCode' | 'shipType'>): VesselCategory {
  if (t.shipTypeCode != null) return categoryForShipType(t.shipTypeCode);
  return vesselCategory(t.shipType);
}

/**
 * Category fill colours. Chosen as MID-TONE hues (not near-white, not near-black)
 * so each one reads on both the light day chart and the near-black night chart
 * without having to swap the palette. The scheme follows the common AIS/ECDIS
 * convention: cargo green, tanker rust, passenger blue, high-speed amber, working/
 * special craft teal, fishing brown, sailing a lighter green, pleasure a violet,
 * and unknown a neutral grey. A CPA threat overrides this with red upstream, and a
 * lost target overrides it with grey, so those two never depend on the type hue.
 */
export const CATEGORY_COLOR: Record<VesselCategory, string> = {
  cargo: '#2f9e5b',
  tanker: '#cf5636',
  passenger: '#4079c9',
  hsc: '#caa11e',
  tug: '#19a7ac',
  fishing: '#b26a2e',
  sailing: '#6fb04a',
  pleasure: '#9a63c9',
  other: '#8b95a0',
};

/** Typical overall length (m) for a category, used when AIS dimensions are absent. */
const DEFAULT_LEN: Record<VesselCategory, number> = {
  tanker: 200,
  cargo: 140,
  passenger: 110,
  hsc: 40,
  tug: 25,
  fishing: 22,
  sailing: 12,
  pleasure: 9,
  other: 30,
};

/**
 * Symbol forward-extent (nm) by overall length bucket, BEFORE the zoom scale. The
 * mark is geographic, so the caller multiplies these by aisSymbolScaleForZoom to
 * hold a sensible on-screen size at any zoom. Deliberately small and closely
 * spaced: this is a size HINT (a big ship reads bigger than a yacht), not a
 * to-scale hull, so it never dominates the chart.
 */
export function sizeNmForLength(lengthM: number): number {
  if (lengthM < 12) return 0.045;
  if (lengthM < 30) return 0.06;
  if (lengthM < 80) return 0.085;
  if (lengthM < 160) return 0.11;
  if (lengthM < 250) return 0.14;
  return 0.18;
}

// Zoom scaling, mirroring the wind barb (barbScaleForZoom): the outline is drawn
// in NM at AIS_REF_ZOOM, and each zoom level in doubles the pixels-per-NM, so to
// hold a roughly constant on-screen size we halve the NM per level in and double
// it per level out — factor = 2^(ref - zoom). That constant on-screen size is
// what keeps a target reading next to the own-ship marker, which is a fixed-pixel
// symbol and so never changes size with zoom.
//
// The min clamp is the one that matters here. Own ship holds its pixel size at
// every zoom; a too-high floor on the AIS scale lets the NM stop shrinking on the
// way in while the pixels-per-NM keep doubling, so the target balloons and dwarfs
// the boat zoomed in (fb-ms1nm3np-1). The floor is set to the same 0.02 the wind
// barb uses, so the size holds constant well past normal close-quarters zoom
// before the clamp ever bites. The max clamp (zoomed right out) keeps a distant
// target from shrinking to nothing.
const AIS_REF_ZOOM = 12.5;
const AIS_MIN_SCALE = 0.02;
const AIS_MAX_SCALE = 8;

/** NM multiplier for the vessel outline at a given map zoom (clamped). */
export function aisSymbolScaleForZoom(zoom: number): number {
  const raw = Math.pow(2, AIS_REF_ZOOM - zoom);
  return Math.min(AIS_MAX_SCALE, Math.max(AIS_MIN_SCALE, raw));
}

/** Below this speed a target is treated as stopped/moored: no meaningful heading. */
export const STOPPED_SOG_KN = 0.2;

export type VesselShape = 'triangle' | 'ship' | 'diamond' | 'sail' | 'dot';

const SHAPE: Record<VesselCategory, VesselShape> = {
  cargo: 'ship',
  tanker: 'ship',
  passenger: 'triangle',
  hsc: 'triangle',
  tug: 'triangle',
  other: 'triangle',
  fishing: 'diamond',
  sailing: 'sail',
  pleasure: 'triangle',
};

/** Outline points in (forward, starboard) units, bow extent = 1, before scaling. */
const OUTLINE: Record<VesselShape, [number, number][]> = {
  triangle: [
    [1, 0],
    [-0.7, 0.62],
    [-0.7, -0.62],
  ],
  // Pointed bow + rectangular hull — reads as a big vessel.
  ship: [
    [1, 0],
    [0.35, 0.42],
    [-0.9, 0.42],
    [-0.9, -0.42],
    [0.35, -0.42],
  ],
  diamond: [
    [1, 0],
    [0, 0.6],
    [-1, 0],
    [0, -0.6],
  ],
  // Slim, longer wedge — reads like a yacht under sail.
  sail: [
    [1.2, 0],
    [-0.55, 0.38],
    [-0.55, -0.38],
  ],
  // Small non-directional diamond for a stopped/moored target: half the extent of
  // the others, so it reads as a quiet little mark rather than a pointing arrow.
  dot: [
    [0.5, 0],
    [0, 0.5],
    [-0.5, 0],
    [0, -0.5],
  ],
};

const R2D = 180 / Math.PI;

/** A point offset from `pos` by forward/starboard nm, given heading (deg true). */
function offset(
  pos: [number, number],
  headingDeg: number,
  fwdNm: number,
  stbdNm: number,
): [number, number] {
  const dist = Math.hypot(fwdNm, stbdNm);
  if (dist < 1e-9) return pos;
  const brg = (headingDeg + Math.atan2(stbdNm, fwdNm) * R2D + 360) % 360;
  return rhumbDestination(pos, brg, dist);
}

export interface VesselSymbol {
  category: VesselCategory;
  shape: VesselShape;
  /** Forward extent of the symbol, nm (a proxy for displayed size). */
  sizeNm: number;
  /** Closed polygon ring ([lon, lat]). */
  ring: [number, number][];
}

/**
 * Build the oriented, type/size-aware outline for a target. `scale` is the zoom
 * multiplier from aisSymbolScaleForZoom (defaults to 1 for tests and callers that
 * don't scale). A stopped target draws as a small, north-aligned diamond, since
 * its heading is meaningless when it isn't moving.
 */
export function vesselSymbol(t: AisTarget, scale = 1): VesselSymbol {
  const category = targetCategory(t);
  const lengthM = t.lengthM && t.lengthM > 0 ? t.lengthM : DEFAULT_LEN[category];
  const stopped = (t.sogKn ?? 0) < STOPPED_SOG_KN;
  // Keep the moored dot a touch smaller still, so a stopped ship doesn't read as a
  // big blob; the size hint matters most for the vessels actually under way.
  const sizeNm = sizeNmForLength(lengthM) * scale * (stopped ? 0.8 : 1);
  const shape = stopped ? 'dot' : SHAPE[category];
  const headingDeg = t.headingDeg ?? t.cogDeg;
  const ring = OUTLINE[shape].map(([f, s]) =>
    offset(t.position, headingDeg, f * sizeNm, s * sizeNm),
  );
  ring.push(ring[0]); // close the ring
  return { category, shape, sizeNm, ring };
}

/**
 * Course/speed vector length (nm): how far the vessel runs in `vectorMin`
 * minutes at its current SOG. Faster vessels get a longer line; a stopped one
 * gets nothing (the caller drops vectors below a small threshold).
 */
export function vectorLengthNm(sogKn: number, vectorMin: number): number {
  return sogKn * (vectorMin / 60);
}
